// src/components/PatternInsights.jsx
// Displays automatically discovered patterns from trade data
import React, { useState } from 'react';
import { TrendingUp, AlertTriangle, Lightbulb, ChevronDown, ChevronUp, Lock } from 'lucide-react';

export default function PatternInsights({ patternData, isPro = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!patternData?.hasData) {
    return (
      <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
          <Lightbulb size={16} style={{ color:'#ffd166' }} />
          <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>Pattern Insights</div>
        </div>
        <div style={{ fontSize:13, color:'#55556a' }}>Log 10+ trades to unlock automatic pattern detection.</div>
      </div>
    );
  }

  const { insights } = patternData;
  const warnings    = insights.filter(i => i.category === 'warning');
  const discoveries = insights.filter(i => i.category === 'discovery');
  const limit    = isPro ? insights.length : 2;
  const visible  = expanded ? insights.slice(0, limit) : insights.slice(0, Math.min(3, limit));

  if (insights.length === 0) return null;

  return (
    <div style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, overflow:'hidden', marginBottom:16 }}>
      {/* Header */}
      <div style={{ padding:'18px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #1e1e2e' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Lightbulb size={16} style={{ color:'#ffd166' }} />
          <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>Pattern Insights</div>
          <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
            {warnings.length > 0 && <span style={{ color:'#ff4d6d', marginRight:8, display:'inline-flex', alignItems:'center', gap:5 }}><AlertTriangle size={11} /> {warnings.length} warning{warnings.length > 1 ? 's' : ''}</span>}
            {discoveries.length > 0 && <span style={{ color:'#00e5a0' }}>✓ {discoveries.length} edge{discoveries.length > 1 ? 's' : ''} found</span>}
          </div>
        </div>
        {insights.length > 3 && (
          <button onClick={() => setExpanded(e => !e)} style={{ background:'transparent', border:'1px solid #1e1e2e', borderRadius:7, padding:'6px 12px', cursor:'pointer', color:'#8888a8', display:'flex', alignItems:'center', gap:5, fontSize:12, fontFamily:"'IBM Plex Sans',sans-serif" }}>
            {expanded ? <><ChevronUp size={11}/> Less</> : <><ChevronDown size={11}/> All {insights.length}</>}
          </button>
        )}
      </div>

      {!isPro && insights.length > 2 && (
        <div style={{ padding:'8px 24px 0', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontSize:11, color:'#ffd166', fontFamily:"'DM Mono',monospace", display:'inline-flex', alignItems:'center', gap:6 }}>
            <Lock size={11} /> {insights.length - 2} more insights available on Pro
          </div>
        </div>
      )}
      {/* Insights */}
      <div style={{ padding:'16px 24px', display:'flex', flexDirection:'column', gap:10 }}>
        {visible.map((insight, i) => (
          <div key={i} style={{ background:'#0a0a0f', border:`1px solid ${insight.color}20`, borderRadius:10, padding:'14px 16px', borderLeft:`3px solid ${insight.color}` }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
              <span style={{ fontSize:18, flexShrink:0, lineHeight:1 }}>{insight.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', fontFamily:"'IBM Plex Sans',sans-serif" }}>{insight.title}</div>
                  <div style={{ fontSize:10, color: insight.category === 'warning' ? '#ff9f43' : '#00e5a0', background: insight.category === 'warning' ? 'rgba(255,159,67,0.1)' : 'rgba(0,229,160,0.1)', border:`1px solid ${insight.category === 'warning' ? 'rgba(255,159,67,0.2)' : 'rgba(0,229,160,0.2)'}`, borderRadius:5, padding:'1px 7px', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.06em' }}>
                    {insight.category === 'warning' ? 'Warning' : 'Edge Found'}
                  </div>
                </div>
                <div style={{ fontSize:11, color:insight.color, fontFamily:"'DM Mono',monospace", marginBottom:6 }}>{insight.detail}</div>
                <div style={{ fontSize:12, color:'#8888a8', lineHeight:1.7 }}>{insight.insight}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
